/**
 * VoyageReadiness — CCD-DASH-014 Phase A · R2 flagship dashboard card.
 *
 * Turns "am I ready for my cruise?" into a defensible, explainable,
 * continuously calculated score. Every point traceable.
 *
 * PO directive: Status text first, Percentage second.
 *
 * Consumes: GET /api/cruises/current/voyage-readiness
 * Design: /app/docs/design/CCD-DASH-014-dashboard-design-sprint.md §2 · R2
 */
import React, { useEffect, useState } from "react";
import { useCruise } from "@/context/CruiseContext";
import { api } from "@/lib/api";
import { ShieldCheck, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import HomeSummaryCard from "./HomeSummaryCard";

const API = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#071e3e";
const GOLD = "#c28e42";

const STATUS_TONE = {
  voyage_ready:  { bg: "#0F5132", ring: "#A7F3D0", label: "text-emerald-700" },
  nearly_ready:  { bg: "#0F2244", ring: "#BFDBFE", label: "text-slate-800" },
  in_planning:   { bg: "#7C4A03", ring: "#FDE68A", label: "text-amber-700" },
  just_starting: { bg: "#4C0519", ring: "#FECDD3", label: "text-rose-700" },
};

function DomainRow({ domain, onDeepLink }) {
  const filled = domain.max ? Math.round((domain.earned / domain.max) * 10) : 0;
  const dots = Array.from({ length: 10 }, (_, i) => i < filled);
  const [open, setOpen] = useState(false);

  return (
    <div className="py-2.5 border-b border-slate-100 last:border-b-0" data-testid={`voyage-readiness-domain-${domain.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 text-left group"
        aria-expanded={open}
      >
        <span className="w-24 shrink-0 font-body text-[12px] font-medium text-slate-700 truncate">
          {domain.label}
        </span>
        <span className="flex-1 flex items-center gap-[3px]" aria-hidden="true">
          {dots.map((on, i) => (
            <span
              key={i}
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: on ? NAVY : "#E2E8F0" }}
            />
          ))}
        </span>
        <span className="w-14 shrink-0 text-right font-body text-[11px] text-slate-500 tabular-nums">
          {domain.earned}/{domain.max}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div className="mt-2 pl-24 space-y-1.5" data-testid={`voyage-readiness-explain-${domain.id}`}>
          {domain.contributions.map((c, i) => (
            <div key={i} className="flex items-start gap-2 text-[12px]">
              <span
                className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${c.earned ? "bg-emerald-500" : "bg-slate-300"}`}
                aria-hidden="true"
              />
              <span className={c.earned ? "text-slate-700" : "text-slate-500"}>
                {c.label}
              </span>
              <span className="ml-auto text-slate-400 tabular-nums">
                {c.earned ? `+${c.points}` : `0/${c.points}`}
              </span>
            </div>
          ))}
          {domain.route && (
            <button
              type="button"
              onClick={() => onDeepLink?.(domain.route)}
              className="mt-1 inline-flex items-center gap-1 font-body text-[11px] font-semibold text-slate-700 hover:text-slate-900"
              data-testid={`voyage-readiness-open-${domain.id}`}
            >
              Open {domain.label} <ChevronRight className="w-3 h-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function VoyageReadiness({ onDataLoaded }) {
  const { setRoute, selectedCruiseId } = useCruise();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let alive = true;
    setState({ loading: true });
    (async () => {
      try {
        // CKB-040 v4.1 · LAW-005 · axios `api` interceptor auto-injects
        // `?voyage_id=<selectedCruiseId>` for `/api/cruises/current/*`.
        const r = await api.get("/api/cruises/current/voyage-readiness");
        if (alive) {
          setState({ loading: false, data: r.data });
          onDataLoaded?.(r.data);
        }
      } catch (e) {
        if (alive) setState({ loading: false, error: String(e?.message || e) });
      }
    })();
    return () => { alive = false; };
  }, [onDataLoaded, selectedCruiseId]);

  if (state.loading) {
    return (
      <HomeSummaryCard
        eyebrow="Voyage Readiness"
        icon={<ShieldCheck className="w-3 h-3" />}
        testId="voyage-readiness-card"
      >
        <div className="h-40 flex items-center justify-center">
          <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
        </div>
      </HomeSummaryCard>
    );
  }

  if (state.error || !state.data) {
    return (
      <HomeSummaryCard
        eyebrow="Voyage Readiness"
        icon={<ShieldCheck className="w-3 h-3" />}
        testId="voyage-readiness-card"
      >
        <div className="text-[12px] text-slate-500">
          Readiness will populate as you plan.
        </div>
      </HomeSummaryCard>
    );
  }

  const { status, percentage, domains } = state.data;
  const tone = STATUS_TONE[status.code] || STATUS_TONE.in_planning;

  return (
    <HomeSummaryCard
      eyebrow="Voyage Readiness"
      icon={<ShieldCheck className="w-3 h-3" />}
      testId="voyage-readiness-card"
    >
      <div className="flex items-start gap-5">
        {/* Status dial — Status text FIRST, percentage SECOND (PO directive). */}
        <div
          className="w-32 shrink-0 rounded-xl p-4 text-white flex flex-col items-center justify-center"
          style={{ background: tone.bg }}
          data-testid="voyage-readiness-dial"
        >
          <div
            className="font-display text-[13px] font-semibold tracking-wide text-center leading-tight"
            data-testid="voyage-readiness-status-label"
          >
            {status.label}
          </div>
          <div
            className="mt-2 font-display text-[36px] font-bold tabular-nums leading-none"
            style={{ color: GOLD }}
            data-testid="voyage-readiness-percentage"
          >
            {percentage}%
          </div>
          <div className="mt-1 font-body text-[10px] uppercase tracking-[0.12em] opacity-70">
            Ready
          </div>
        </div>
        {/* Domain rows */}
        <div className="flex-1 min-w-0" data-testid="voyage-readiness-domains">
          {domains.map((d) => (
            <DomainRow key={d.id} domain={d} onDeepLink={setRoute} />
          ))}
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-slate-100 font-body text-[11px] text-slate-500">
        Every score contribution links to its workspace. Click any row to see why.
      </div>
    </HomeSummaryCard>
  );
}

export default VoyageReadiness;
